// DateInput — consistent date entry wrapper.
//
// Design:
// - Uses the native <input type="date"> under the hood to preserve the browser's
//   date picker UX (locale-aware calendar, keyboard navigation for date
//   selection, accessibility). Native date inputs are strong for dates; the
//   custom segmented TimeInput pattern is not the right fit here because the
//   browser already provides excellent date-selection ergonomics.
// - Value is always ISO "YYYY-MM-DD" or "" (matches native input's value
//   contract). Consumers never deal with locale formatting.
// - Optional `min`/`max` provide per-field range constraints (e.g. "must be
//   after bore spud date"). Consumers can also layer app-level validation on
//   top via onChange — `min`/`max` are a hint to the picker, not a hard lock.
// - A single `date-input-widget` class gives every date field a consistent look
//   across forms. `className` is forwarded for local layout overrides.
//
// Future: if the project later decides to swap to a fully segmented picker
// (matching TimeInput), the change is a single-file internal swap — every
// caller keeps the same props contract.

import type { FC, KeyboardEvent } from "react";
import "./DateInput.css";

export interface DateInputProps {
  /** ISO date string "YYYY-MM-DD", or "" for no value. */
  value: string;
  /** Called with a new ISO "YYYY-MM-DD" (or "" when cleared). */
  onChange: (value: string) => void;
  className?: string;
  id?: string;
  "aria-label"?: string;
  disabled?: boolean;
  /** Minimum selectable date, ISO "YYYY-MM-DD". */
  min?: string;
  /** Maximum selectable date, ISO "YYYY-MM-DD". */
  max?: string;
  /** Tooltip text (hover title). */
  title?: string;
  /** Forwarded to the underlying input — used by table cell grids for arrow-key navigation. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export const DateInput: FC<DateInputProps> = ({
  value,
  onChange,
  className,
  id,
  "aria-label": ariaLabel,
  disabled = false,
  min,
  max,
  title,
  onKeyDown,
}) => {
  return (
    <input
      type="date"
      className={`date-input-widget${className ? ` ${className}` : ""}`}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel ?? "Date input"}
      disabled={disabled}
      min={min}
      max={max}
      title={title}
    />
  );
};

export default DateInput;
